import { createApiService, type ApiError } from './api';
import type {
  PromptSummary,
  PromptDetail,
  RunPromptRequest,
  RunPromptResponse,
  ResyncOptions,
  ResyncResponse,
  RunWithUserRequest,
} from '@/types/prompt';
import type { PaginatedResponse } from '@/types/common';
import { transformSpringDataPagination } from '@/utils/pagination';
import { unwrapPaginatedResponse } from '@/utils/pagination';

// Relative base so nginx (location /api/prompt/) and Vite proxy route to prompt service (18585)
const PROMPT_API_BASE_URL =
  import.meta.env.VITE_PROMPT_API_BASE_URL || '/api/prompt';

/**
 * Path prefix used for prompt service endpoints.
 * Backend receives the path after nginx strips /api/prompt, so use what the service expects.
 * Verify against http://localhost:18585/api-docs (e.g. /api/v1, /v1, or empty).
 */
const PROMPT_PATH_PREFIX = '/api/v1';

const promptApiService = createApiService(PROMPT_API_BASE_URL);

function buildPath(segment: string): string {
  return `${PROMPT_PATH_PREFIX}${segment}`;
}

/** Unwrap API response: { success, message, data } -> data, or return as-is if already prompt. */
function unwrapPromptResponse(
  res: PromptDetail | { success?: boolean; data?: PromptDetail; message?: string }
): PromptDetail {
  if (res && typeof res === 'object' && 'data' in res && res.data != null) {
    return res.data as PromptDetail;
  }
  return res as PromptDetail;
}

/** Timeout (ms) for list request so the UI does not hang when the prompt service is unreachable. */
const LIST_REQUEST_TIMEOUT_MS = 10_000;

export const promptApi = {
  /** List prompts (paginated). Handles Spring Page, raw array, or { data } wrapper. */
  list: async (params?: {
    page?: number;
    size?: number;
    sortBy?: string;
    sortDirection?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<PromptSummary>> => {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', String(params.page));
    if (params?.size !== undefined) queryParams.append('size', String(params.size));
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortDirection) queryParams.append('sortDirection', params.sortDirection);
    const query = queryParams.toString();
    const url = buildPath(`/prompts/search${query ? `?${query}` : ''}`);
    const signal =
      typeof AbortSignal !== 'undefined' && 'timeout' in AbortSignal
        ? AbortSignal.timeout(LIST_REQUEST_TIMEOUT_MS)
        : undefined;
    try {
      const response = await promptApiService.get<unknown>(url, { signal });
      const normalized = unwrapPaginatedResponse<PromptSummary>(response);
      return transformSpringDataPagination<PromptSummary>(normalized);
    } catch (e) {
      const err = e as ApiError;
      if (err?.status === 404) {
        return { content: [], page: { size: 20, number: 0, totalElements: 0, totalPages: 0 } };
      }
      throw e;
    }
  },

  /** Get one prompt by doc id. API: GET /api/v1/prompts/doc/:id */
  getById: async (id: string): Promise<PromptDetail> => {
    const res = await promptApiService.get<PromptDetail | { success?: boolean; data?: PromptDetail; message?: string }>(
      buildPath(`/prompts/doc/${encodeURIComponent(id)}`)
    );
    return unwrapPromptResponse(res);
  },

  /** Get one prompt by key. API: GET /api/v1/prompts/:key (e.g. assessment-content-customization) */
  getByKey: async (key: string): Promise<PromptDetail> => {
    const res = await promptApiService.get<PromptDetail | { success?: boolean; data?: PromptDetail; message?: string }>(
      buildPath(`/prompts/${encodeURIComponent(key)}`)
    );
    return unwrapPromptResponse(res);
  },

  /**
   * Get one prompt by key or doc id. Tries key first (e.g. assessment-content-customization), then doc id on 404.
   */
  get: async (keyOrId: string): Promise<PromptDetail> => {
    try {
      const res = await promptApiService.get<PromptDetail | { success?: boolean; data?: PromptDetail; message?: string }>(
        buildPath(`/prompts/${encodeURIComponent(keyOrId)}`)
      );
      return unwrapPromptResponse(res);
    } catch (e) {
      const err = e as ApiError;
      if (err?.status === 404) {
        const res = await promptApiService.get<PromptDetail | { success?: boolean; data?: PromptDetail; message?: string }>(
          buildPath(`/prompts/doc/${encodeURIComponent(keyOrId)}`)
        );
        return unwrapPromptResponse(res);
      }
      throw e;
    }
  },

  /** Run/execute prompt (test). API: POST /api/v1/prompts/doc/:id/run */
  run: async (id: string, body: RunPromptRequest): Promise<RunPromptResponse> => {
    const res = await promptApiService.post<RunPromptResponse | { data?: RunPromptResponse }>(
      buildPath(`/prompts/doc/${encodeURIComponent(id)}/run`),
      body
    );
    if (res && typeof res === 'object' && 'data' in res && res.data != null) {
      return res.data as RunPromptResponse;
    }
    return res as RunPromptResponse;
  },

  /** Run prompt with user context (user-specific test). API: POST /api/v1/prompts/doc/:id/run */
  runWithUser: async (
    id: string,
    body: RunWithUserRequest
  ): Promise<RunPromptResponse> => {
    const res = await promptApiService.post<RunPromptResponse | { data?: RunPromptResponse }>(
      buildPath(`/prompts/doc/${encodeURIComponent(id)}/run`),
      body
    );
    if (res && typeof res === 'object' && 'data' in res && res.data != null) {
      return res.data as RunPromptResponse;
    }
    return res as RunPromptResponse;
  },

  /** Resync this prompt from release tag. API: POST /api/v1/prompts/doc/:id/resync */
  resync: async (
    id: string,
    options?: ResyncOptions
  ): Promise<ResyncResponse> => {
    const params = new URLSearchParams();
    if (options?.releaseTag) params.set('releaseTag', options.releaseTag);
    const query = params.toString();
    const res = await promptApiService.post<ResyncResponse | { data?: ResyncResponse }>(
      buildPath(`/prompts/doc/${encodeURIComponent(id)}/resync${query ? `?${query}` : ''}`),
      {}
    );
    if (res && typeof res === 'object' && 'data' in res && res.data != null) {
      return res.data as ResyncResponse;
    }
    return res as ResyncResponse;
  },

  /** Full resync: sync all prompts from release tag. */
  fullResync: async (options?: ResyncOptions): Promise<ResyncResponse> => {
    const params = new URLSearchParams();
    if (options?.releaseTag) params.set('releaseTag', options.releaseTag);
    const query = params.toString();
    const res = await promptApiService.post<ResyncResponse | { data?: ResyncResponse }>(
      buildPath(`/prompts/resync${query ? `?${query}` : ''}`),
      {}
    );
    if (res && typeof res === 'object' && 'data' in res && res.data != null) {
      return res.data as ResyncResponse;
    }
    return res as ResyncResponse;
  },
};

export { promptApiService };
